import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ArrowRightIcon, CheckIcon, SearchIcon, SparklesIcon, SendIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
// New Sub-components
import { HeroPreview } from '../components/landing/HeroPreview';
import { ProductSuite } from '../components/landing/ProductSuite';
import { OpenclawCowork } from '../components/landing/OpenclawCowork';
import { OpenclawOutput } from '../components/landing/OpenclawOutput';
import { DataFields } from '../components/landing/DataFields';
import { PowerfulUseCases } from '../components/landing/PowerfulUseCases';
import { ProductDemo } from '../components/landing/ProductDemo';
import { DeveloperAPI } from '../components/landing/DeveloperAPI';
import { DataQualityCompliance } from '../components/landing/DataQualityCompliance';
import { CoreFeatures } from '../components/landing/CoreFeatures';
// DataFields removed
import { PricingSection } from '../components/landing/PricingSection';
import { Testimonials } from '../components/landing/Testimonials';
import { FAQSection } from '../components/landing/FAQSection';
import { AnnouncementBar } from '../components/landing/AnnouncementBar';
import { CategoryList } from '../components/landing/CategoryList';
import { Button } from '../components/ui/Button';
// Animated counter hook
const useCounter = (end: number, duration: number = 2000, suffix: string = '') => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const startCounting = useCallback(() => {
    if (hasAnimated) return;
    setHasAnimated(true);
    const startTime = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(end * easeOutQuart));
      if (progress === 1) {
        clearInterval(timer);
      }
    }, 16);
  }, [hasAnimated, end, duration]);
  return {
    count: count + suffix,
    startCounting
  };
};
// StatCard component
const StatCard = ({
  stat,
  index,
  isLast
}) => {
  const {
    label,
    value,
    suffix
  } = stat;
  const {
    count,
    startCounting
  } = useCounter(value, 2000, suffix);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        startCounting();
      }
    }, {
      threshold: 0.5
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startCounting]);
  return <div ref={ref} className="relative flex items-center justify-center" data-id="element-6526">
      {/* Vertical divider (not on first item) */}
      {index > 0 && <div className="absolute left-0 top-1/2 -translate-y-1/2 h-12 w-px bg-slate-200 hidden md:block" data-id="element-6527" />}
      <div className="flex flex-col items-center justify-center text-center px-4" data-id="element-6528">
        <motion.div animate={{
        opacity: [1, 0.5, 1],
        scale: [1, 1.05, 1]
      }} transition={{
        duration: 2.5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: index * 0.4
      }} className="text-4xl md:text-5xl font-bold text-blue-600 mb-2 tracking-tight" data-id="element-6529">
          {count}
        </motion.div>
        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest" data-id="element-6530">
          {label}
        </div>
      </div>
    </div>;
};
export function Landing() {
  const stats = [{
    label: 'Local businesses',
    value: 200,
    suffix: 'M+'
  }, {
    label: 'Business categories',
    value: 500,
    suffix: '+'
  }, {
    label: 'Data fields per business',
    value: 50,
    suffix: '+'
  }, {
    label: 'Data accuracy',
    value: 97,
    suffix: '-99%'
  }];
  const steps = [{
    number: '1',
    title: 'Search & filter',
    description: 'Use our database or Google Maps scraper to find your ideal prospects.'
  }, {
    number: '2',
    title: 'Enrich & verify',
    description: 'Automatically append verified contact data to your business lists.'
  }, {
    number: '3',
    title: 'Reach & convert',
    description: 'Launch email sequences or export audiences for ad campaigns.'
  }];
  const pressBadges = [{
    name: 'Y Combinator',
    logo: 'https://cdn.magicpatterns.com/uploads/gYArx9LnCL1zUGZHPNRF8U/Y_Combinator_logo_text_wordmark.png',
    label: 'Backed by'
  }, {
    name: 'TechCrunch',
    logo: 'https://cdn.magicpatterns.com/uploads/3bCBHE6nPmewusss3BgLVF/techcrunch-logo.png.webp',
    label: null
  }, {
    name: 'Forbes',
    logo: 'https://cdn.magicpatterns.com/uploads/9arX29ehiKEbnF8gb5xKPQ/Forbes_logo.svg.png',
    label: 'Featured by'
  }];
  const popularCategories = ['Restaurants', 'Plumbers', 'Dentists', 'HVAC', 'Electricians', 'Law Firms', 'Real Estate', 'Auto Repair', 'Hair Salons', 'Accountants'];
  return <div className="min-w-full bg-slate-50/50 ecom-aeonik flex flex-col min-h-screen" style={{
    fontFamily: 'Aeonik, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }} data-id="element-6531">
      <AnnouncementBar data-id="element-6532" />
      <Navbar data-id="element-6533" />

      <main className="flex-grow pt-12 pb-24 space-y-12 md:space-y-24" data-id="element-6534">
        {/* HERO SECTION */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-id="element-6535">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 pt-28 pb-0 px-0 text-center relative overflow-hidden" data-id="element-6536">
            {/* Subtle background blob — continuous float */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-50" data-id="element-6537">
              <motion.div animate={{
              x: [0, 60, -40, 0],
              y: [0, -40, 30, 0],
              scale: [1, 1.2, 0.85, 1]
            }} transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-[-10%] left-[20%] w-[60%] h-[60%] rounded-full bg-blue-200 blur-[100px]" data-id="element-6538" />
            </div>

            {/* Subtle dot pattern overlay */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle, #156ef6 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }} data-id="element-6539"></div>

            <div className="relative z-10 max-w-4xl mx-auto space-y-8 px-4 sm:px-6 lg:px-8 pb-16" data-id="element-6540">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1,
              y: [0, -6, 0]
            }} transition={{
              opacity: {
                duration: 0.4,
                ease: 'easeOut'
              },
              scale: {
                duration: 0.4,
                ease: 'easeOut'
              },
              y: {
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            }} className="inline-flex items-center gap-2 px-6 py-3 bg-white/60 backdrop-blur-md text-blue-600 rounded-full text-lg sm:text-xl font-semibold shadow-sm border border-slate-200/60" data-id="element-6541">
                <h1 className="flex items-center gap-2 text-lg sm:text-xl font-semibold" data-id="element-6542">
                  <MapPinIcon className="w-5 h-5" data-id="element-6543" />
                  200M+ local business database
                </h1>
              </motion.div>

              <div className="text-5xl md:text-7xl tracking-tight leading-[1.1] font-bold" data-id="element-6544">
                <motion.span initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} transition={{
                duration: 0.5,
                delay: 0.1,
                ease: 'easeOut'
              }} className="text-blue-600" data-id="element-6545">
                  Openclaw
                </motion.span>{' '}
                <motion.span initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} transition={{
                duration: 0.5,
                delay: 0.2,
                ease: 'easeOut'
              }} className="text-slate-900" data-id="element-6546">
                  for
                </motion.span>{' '}
                <motion.span initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} transition={{
                duration: 0.5,
                delay: 0.3,
                ease: 'easeOut'
              }} className="text-blue-600" data-id="element-6547">
                  sales
                </motion.span>
              </div>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.5,
              ease: 'easeOut'
            }} className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed" data-id="element-6548">
                Access comprehensive local business data with verified emails,
                phones, and decision-maker details. Power your outreach with
                AI-enriched intelligence.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.6,
              ease: 'easeOut'
            }} className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4" data-id="element-6549">
                <Button href="http://app.openmart.com/register" variant="primary" size="lg" arrow fullWidth className="sm:w-auto" data-id="element-6550">
                  Try for free
                </Button>
                <Button href="http://app.openmart.com/register" variant="secondary" size="lg" fullWidth className="sm:w-auto" data-id="element-6551">
                  Book a demo
                </Button>
              </motion.div>

              {/* Trust Badges */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.8
            }} className="pt-8 flex flex-wrap items-center justify-center gap-8 md:gap-12" data-id="element-6552">
                {pressBadges.map((badge, index) => <div key={index} className="flex items-center gap-2.5" data-id="element-6553">
                    {badge.label && <span className="text-xs text-slate-400 font-medium whitespace-nowrap" data-id="element-6554">
                        {badge.label}
                      </span>}
                    <img src={badge.logo} alt={`${badge.name} logo`} className="h-5 md:h-6 max-w-[130px] object-contain opacity-80 hover:opacity-100 transition-opacity" data-id="element-6555" />
                  </div>)}
              </motion.div>
            </div>

            {/* Animated Contact Ticker (blue illustration) */}
            <HeroPreview data-id="element-6556" />
          </div>
        </section>

        {/* STATS BAR — line style with dividers */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6" data-id="element-6557">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12" data-id="element-6558">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12" data-id="element-6559">
              {stats.map((stat, index) => <StatCard key={index} stat={stat} index={index} isLast={index === stats.length - 1} data-id="element-6560" />)}
            </div>

            {/* Search Bar */}
            <div className="mt-10 pt-10 border-t border-slate-100" data-id="element-6561">
              <div className="max-w-3xl mx-auto" data-id="element-6562">
                <div className="relative" data-id="element-6563">
                  <motion.div animate={{
                  opacity: [0.05, 0.15, 0.05],
                  scale: [1, 1.02, 1]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="absolute -inset-1 bg-blue-500 rounded-2xl blur-xl pointer-events-none" data-id="element-6564" />
                  <div className="relative bg-slate-50 rounded-xl border border-slate-200/60 p-4" data-id="element-6565">
                    <div className="flex flex-col sm:flex-row gap-2" data-id="element-6566">
                      <div className="flex-1 relative" data-id="element-6567">
                        <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" data-id="element-6568" />
                        <input type="text" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all" placeholder="Business type (e.g. HVAC, restaurants)" data-id="element-6569" />
                      </div>
                      <div className="flex-1 relative" data-id="element-6570">
                        <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" data-id="element-6571" />
                        <input type="text" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all" placeholder="City (e.g. Austin, TX)" data-id="element-6572" />
                      </div>
                      <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-full font-bold text-sm hover:bg-blue-700 transition-all shadow-sm whitespace-nowrap" data-id="element-6573">
                        Find leads
                        <ArrowRightIcon className="w-3.5 h-3.5 transform group-hover:translate-x-0.5 transition-transform" data-id="element-6574" />
                      </a>
                    </div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap" data-id="element-6575">
                      <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider" data-id="element-6576">
                        Popular:
                      </span>
                      {popularCategories.slice(0, 6).map((cat, i) => <a key={i} href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="text-[11px] text-slate-400 hover:text-blue-600 transition-colors cursor-pointer" data-id="element-6577">
                          {cat}
                          {i < 5 ? ' ·' : ''}
                        </a>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PRODUCT SUITE */}
        <ProductSuite data-id="element-6578" />

        {/* OPENCLAW COWORK */}
        <OpenclawCowork data-id="element-6579" />

        {/* OPENCLAW OUTPUT */}
        <OpenclawOutput data-id="element-6580" />

        {/* DATA FIELDS */}
        <DataFields data-id="element-6581" />

        {/* POWERFUL USE CASES */}
        <PowerfulUseCases data-id="element-6582" />

        {/* PRODUCT DEMO */}
        <ProductDemo data-id="element-6583" />

        {/* HOW IT WORKS */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6584">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16" data-id="element-6585">
            <div className="text-center mb-16" data-id="element-6586">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-6587">
                How it <span className="text-blue-600" data-id="element-6588">works</span>
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-lg text-slate-500" data-id="element-6589">
                Three simple steps to start reaching local businesses.
              </motion.p>
            </div>

            {/* Step Indicator */}
            <div className="hidden md:flex items-center justify-center mb-12 px-16" data-id="element-6590">
              {steps.map((step, index) => <Fragment key={index} data-id="element-6591">
                  <motion.div initial={{
                opacity: 0,
                scale: 0
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.2,
                type: 'spring',
                stiffness: 200
              }} className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-blue-600/20 shrink-0" data-id="element-6592">
                    {step.number}
                  </motion.div>
                  {index < steps.length - 1 && <motion.div initial={{
                scaleX: 0
              }} whileInView={{
                scaleX: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: index * 0.2 + 0.2
              }} className="flex-1 h-0.5 bg-blue-600 origin-left mx-1" data-id="element-6593" />}
                </Fragment>)}
            </div>

            {/* Step Cards */}
            <div className="grid md:grid-cols-3 gap-6" data-id="element-6594">
              {steps.map((step, index) => {
              const StepIcon = index === 0 ? SearchIcon : index === 1 ? SparklesIcon : SendIcon;
              return <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.15
              }} className="bg-white rounded-3xl border border-slate-200 p-8 flex flex-col hover:border-blue-200 transition-colors group" data-id="element-6595">
                    {/* Icon */}
                    <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-6 group-hover:bg-blue-50 group-hover:border-blue-100 transition-colors" data-id="element-6596">
                      <StepIcon className="w-7 h-7 text-blue-600" data-id="element-6597" />
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-6598">
                      {step.title}
                    </h3>
                    <p className="text-slate-500 leading-relaxed mb-8" data-id="element-6599">
                      {step.description}
                    </p>

                    {/* Separator */}
                    <div className="h-px bg-slate-100 mb-6 mt-auto" data-id="element-6600" />

                    {/* Mini Illustration */}
                    {index === 0 && <motion.div initial={{
                  opacity: 0
                }} whileInView={{
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.4
                }} className="bg-slate-50 rounded-xl p-4 space-y-3" data-id="element-6601">
                        <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-100" data-id="element-6602">
                          <SearchIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-6603" />
                          <motion.div initial={{
                      width: 0
                    }} whileInView={{
                      width: '60%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.8,
                      delay: 0.6
                    }} className="h-2.5 bg-slate-200 rounded-full" data-id="element-6604" />
                        </div>
                        <div className="flex gap-2" data-id="element-6605">
                          {['bg-blue-100', 'bg-slate-100', 'bg-slate-100'].map((bg, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} whileInView={{
                      opacity: 1,
                      scale: 1
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.3,
                      delay: 0.8 + i * 0.1
                    }} className={`${bg} rounded-md px-3 py-1.5 h-6 w-16`} data-id="element-6606" />)}
                        </div>
                      </motion.div>}

                    {index === 1 && <motion.div initial={{
                  opacity: 0
                }} whileInView={{
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.4
                }} className="bg-slate-50 rounded-xl p-4 space-y-3" data-id="element-6607">
                        <div className="flex items-center gap-3" data-id="element-6608">
                          <div className="w-8 h-8 rounded-full bg-slate-200 shrink-0" data-id="element-6609" />
                          <div className="space-y-1.5 flex-1" data-id="element-6610">
                            <motion.div initial={{
                        width: 0
                      }} whileInView={{
                        width: '70%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.6,
                        delay: 0.6
                      }} className="h-2.5 bg-slate-200 rounded-full" data-id="element-6611" />
                            <motion.div initial={{
                        width: 0
                      }} whileInView={{
                        width: '50%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.6,
                        delay: 0.7
                      }} className="h-2 bg-slate-100 rounded-full" data-id="element-6612" />
                          </div>
                        </div>
                        <div className="space-y-2 pt-1" data-id="element-6613">
                          <div className="flex items-center gap-2" data-id="element-6614">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" data-id="element-6615" />
                            <motion.div initial={{
                        width: 0
                      }} whileInView={{
                        width: '85%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.7,
                        delay: 0.8
                      }} className="h-2 bg-blue-200 rounded-full" data-id="element-6616" />
                          </div>
                          <div className="flex items-center gap-2" data-id="element-6617">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" data-id="element-6618" />
                            <motion.div initial={{
                        width: 0
                      }} whileInView={{
                        width: '65%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.7,
                        delay: 0.9
                      }} className="h-2 bg-blue-200 rounded-full" data-id="element-6619" />
                          </div>
                        </div>
                      </motion.div>}

                    {index === 2 && <motion.div initial={{
                  opacity: 0
                }} whileInView={{
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.4
                }} className="bg-slate-50 rounded-xl p-4 space-y-3" data-id="element-6620">
                        <div className="flex items-center justify-between" data-id="element-6621">
                          <div className="space-y-1.5 flex-1" data-id="element-6622">
                            <motion.div initial={{
                        width: 0
                      }} whileInView={{
                        width: '80%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.6,
                        delay: 0.6
                      }} className="h-2.5 bg-slate-200 rounded-full" data-id="element-6623" />
                            <motion.div initial={{
                        width: 0
                      }} whileInView={{
                        width: '60%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.6,
                        delay: 0.7
                      }} className="h-2 bg-slate-100 rounded-full" data-id="element-6624" />
                          </div>
                          <motion.div initial={{
                      opacity: 0,
                      scale: 0.8
                    }} whileInView={{
                      opacity: 1,
                      scale: 1
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.3,
                      delay: 0.8
                    }} className="bg-blue-600 rounded-md px-4 py-1.5 ml-4" data-id="element-6625">
                            <div className="h-2.5 w-8 bg-white rounded-full" data-id="element-6626" />
                          </motion.div>
                        </div>
                        <div className="space-y-2 pt-1" data-id="element-6627">
                          {[0.9, 0.75, 0.6].map((w, i) => <motion.div key={i} initial={{
                      width: 0
                    }} whileInView={{
                      width: `${w * 100}%`
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.5,
                      delay: 0.9 + i * 0.1
                    }} className="h-2 bg-slate-200 rounded-full" data-id="element-6628" />)}
                        </div>
                      </motion.div>}
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* DEVELOPER API */}
        <DeveloperAPI data-id="element-6629" />

        {/* DATA QUALITY & COMPLIANCE */}
        <DataQualityCompliance data-id="element-6630" />

        {/* CORE FEATURES */}
        <CoreFeatures data-id="element-6631" />

        {/* CATEGORY LIST */}
        <CategoryList data-id="element-6632" />

        {/* PRICING */}
        <PricingSection data-id="element-6633" />

        {/* TESTIMONIALS (with integrated company logo ticker) */}
        <Testimonials data-id="element-6634" />

        {/* FAQ */}
        <FAQSection data-id="element-6635" />

        {/* FINAL CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6636">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-600 rounded-[2.5rem] py-20 md:py-24 px-4 sm:px-6 lg:px-8 text-center shadow-xl relative overflow-hidden" data-id="element-6637">
            {/* Floating ambient blobs */}
            <motion.div animate={{
            x: [0, 60, -50, 0],
            y: [0, -50, 35, 0],
            scale: [1, 1.3, 0.85, 1]
          }} transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500 blur-[80px] opacity-40 pointer-events-none" data-id="element-6638" />
            <motion.div animate={{
            x: [0, -50, 35, 0],
            y: [0, 35, -40, 0],
            scale: [1, 0.85, 1.25, 1]
          }} transition={{
            duration: 13,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute bottom-[-20%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-400 blur-[60px] opacity-35 pointer-events-none" data-id="element-6639" />
            <h2 className="text-3xl md:text-4xl text-white mb-8 max-w-3xl mx-auto font-bold relative z-10" data-id="element-6640">
              Start reaching local businesses today
            </h2>

            <div className="max-w-md mx-auto relative z-10" data-id="element-6641">
              <div className="mb-8" data-id="element-6642">
                <Button href="http://app.openmart.com/register" variant="secondary" size="lg" fullWidth className="sm:w-auto !text-blue-600" data-id="element-6643">
                  Try for free
                </Button>
              </div>

              <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-semibold text-white" data-id="element-6644">
                <span className="flex items-center gap-2" data-id="element-6645">
                  <CheckIcon className="w-4 h-4 text-white" data-id="element-6646" /> Free credits
                </span>
                <span className="flex items-center gap-2" data-id="element-6647">
                  <CheckIcon className="w-4 h-4 text-white" data-id="element-6648" /> No credit card
                </span>
                <span className="flex items-center gap-2" data-id="element-6649">
                  <CheckIcon className="w-4 h-4 text-white" data-id="element-6650" /> Cancel anytime
                </span>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-6651" />
    </div>;
}